/**
 * Obstruction Wizard Form Hook
 *
 * Mantine `useForm` wrapper for the obstruction wizard. Owns the per-field
 * validation rules (length limits, email format, date order) and step
 * navigation helpers. Anchor point is NOT managed by Mantine — it lives in
 * `state.obstructions.formAnchorPoint`.
 */
import { useCallback } from 'react';
import { useForm } from '@mantine/form';
import type { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';

import {
  OBSTRUCTION_AFFECTED_ROAD_LOCATION_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_COMPANY_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_EMAIL_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_MANAGER_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_NOTES_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_PERMIT_NUMBER_MAX_LENGTH,
  OBSTRUCTION_CONTRACTOR_PHONE_MAX_LENGTH,
  OBSTRUCTION_DESCRIPTION_MAX_LENGTH,
  OBSTRUCTION_LOCATION_MAX_LENGTH,
  OBSTRUCTION_MAX_AFFECTED_ROADS,
  OBSTRUCTION_TITLE_MAX_LENGTH,
} from '@/constants/obstructions';
import {
  ObstructionType,
  SeverityLevel,
  TrafficImpact,
} from '@/types/obstructions';
import type { Obstruction } from '@/types/obstructions';

import { isValidEmail } from '@/utils/helpers';

import type {
  ObstructionAffectedRoadInput,
  ObstructionWizardFormState,
} from '../types';
import { OBSTRUCTION_STEP_LOGIC } from '../utils/validation/stepValidation';
import { dateToIsoLocal } from '../utils/dateFormat';

/** Map an existing obstruction to wizard form values (edit mode). */
export function obstructionToFormValues(
  obstruction: Obstruction
): ObstructionWizardFormState {
  return {
    currentStep: 1,
    title: obstruction.title,
    type: obstruction.type,
    severity: obstruction.severity,
    location: obstruction.location ?? '',
    description: obstruction.description ?? '',
    plannedStartDate: dateToIsoLocal(obstruction.plannedStartDate),
    plannedEndDate: dateToIsoLocal(obstruction.plannedEndDate),
    impact: obstruction.impact,
    contractorCompany: obstruction.contractor.company,
    contractorManager: obstruction.contractor.manager,
    contractorPhone: obstruction.contractor.phone,
    contractorEmail: obstruction.contractor.email ?? '',
    contractorEmergencyContact: obstruction.contractor.emergencyContact ?? '',
    contractorPermitNumber: obstruction.contractor.permitNumber ?? '',
    contractorPermitIssuedDate: obstruction.contractor.permitIssuedDate ?? '',
    contractorPermitExpiryDate: obstruction.contractor.permitExpiryDate ?? '',
    contractorNotes: obstruction.contractor.notes ?? '',
    affectedRoads: obstruction.affectedRoads.map((item) => ({
      id: item.id,
      tempId: item.id,
      location: item.location,
      description: item.description ?? '',
    })),
    // Existing attachments live on the obstruction; the form only tracks NEW
    // file selections, so this always starts empty.
    attachments: [],
    anchorPoint: obstruction.anchorPoint,
  };
}

/** Default form values for create mode. */
export function getDefaultObstructionFormValues(): ObstructionWizardFormState {
  return {
    currentStep: 1,
    title: '',
    type: ObstructionType.Maintenance,
    severity: SeverityLevel.Medium,
    location: '',
    description: '',
    plannedStartDate: '',
    plannedEndDate: '',
    impact: TrafficImpact.NoRestriction,
    contractorCompany: '',
    contractorManager: '',
    contractorPhone: '',
    contractorEmail: '',
    contractorEmergencyContact: '',
    contractorPermitNumber: '',
    contractorPermitIssuedDate: '',
    contractorPermitExpiryDate: '',
    contractorNotes: '',
    affectedRoads: [],
    attachments: [],
    anchorPoint: null,
  };
}

export interface UseObstructionFormReturn {
  form: UseFormReturnType<ObstructionWizardFormState>;
  /** Jump to a section by its 1-indexed position (free navigation). */
  goToStep: (step: number) => boolean;
}

interface UseObstructionFormProps {
  initialValues?: ObstructionWizardFormState;
}

export function useObstructionForm({
  initialValues,
}: UseObstructionFormProps = {}): UseObstructionFormReturn {
  const { t } = useTranslation();

  const form = useForm<ObstructionWizardFormState>({
    initialValues: initialValues ?? getDefaultObstructionFormValues(),
    // Real-time validation — validate as the user types and on blur, mirroring
    // the task wizard (`mantineFormSchema.ts`).
    validateInputOnBlur: true,
    validateInputOnChange: true,
    validate: {
      title: (value: string) => {
        const trimmed = value?.trim() ?? '';
        if (trimmed.length === 0) {
          return t('obstructions.wizard.validation.titleRequired');
        }
        if (trimmed.length > OBSTRUCTION_TITLE_MAX_LENGTH) {
          return t('obstructions.wizard.validation.titleMaxLength', {
            max: OBSTRUCTION_TITLE_MAX_LENGTH,
          });
        }
        return null;
      },
      type: (value: ObstructionType) => {
        if (!value || !Object.values(ObstructionType).includes(value)) {
          return t('obstructions.wizard.validation.typeRequired');
        }
        return null;
      },
      severity: (value: SeverityLevel) => {
        if (!value || !Object.values(SeverityLevel).includes(value)) {
          return t('obstructions.wizard.validation.severityRequired');
        }
        return null;
      },
      location: (value: string) => {
        if (value && value.length > OBSTRUCTION_LOCATION_MAX_LENGTH) {
          return t('obstructions.wizard.validation.locationMaxLength', {
            max: OBSTRUCTION_LOCATION_MAX_LENGTH,
          });
        }
        return null;
      },
      description: (value: string) => {
        if (value && value.length > OBSTRUCTION_DESCRIPTION_MAX_LENGTH) {
          return t('obstructions.wizard.validation.descriptionMaxLength', {
            max: OBSTRUCTION_DESCRIPTION_MAX_LENGTH,
          });
        }
        return null;
      },
      plannedStartDate: (value: string) => {
        if (!value) {
          return t('obstructions.wizard.validation.startRequired');
        }
        return null;
      },
      plannedEndDate: (value: string, allValues) => {
        if (!value) {
          return t('obstructions.wizard.validation.endRequired');
        }
        const start = allValues.plannedStartDate;
        if (start) {
          const startMs = Date.parse(start);
          const endMs = Date.parse(value);
          if (
            Number.isFinite(startMs) &&
            Number.isFinite(endMs) &&
            endMs <= startMs
          ) {
            return t('obstructions.wizard.validation.endAfterStart');
          }
        }
        return null;
      },
      impact: (value: TrafficImpact) => {
        if (!value || !Object.values(TrafficImpact).includes(value)) {
          return t('obstructions.wizard.validation.impactRequired');
        }
        return null;
      },
      contractorCompany: (value: string) => {
        const trimmed = value?.trim() ?? '';
        if (trimmed.length === 0) {
          return t('obstructions.wizard.validation.companyRequired');
        }
        if (trimmed.length > OBSTRUCTION_CONTRACTOR_COMPANY_MAX_LENGTH) {
          return t('obstructions.wizard.validation.tooLong', {
            max: OBSTRUCTION_CONTRACTOR_COMPANY_MAX_LENGTH,
          });
        }
        return null;
      },
      contractorManager: (value: string) => {
        const trimmed = value?.trim() ?? '';
        if (trimmed.length === 0) {
          return t('obstructions.wizard.validation.managerRequired');
        }
        if (trimmed.length > OBSTRUCTION_CONTRACTOR_MANAGER_MAX_LENGTH) {
          return t('obstructions.wizard.validation.tooLong', {
            max: OBSTRUCTION_CONTRACTOR_MANAGER_MAX_LENGTH,
          });
        }
        return null;
      },
      contractorPhone: (value: string) => {
        const trimmed = value?.trim() ?? '';
        if (trimmed.length === 0) {
          return t('obstructions.wizard.validation.phoneRequired');
        }
        if (trimmed.length > OBSTRUCTION_CONTRACTOR_PHONE_MAX_LENGTH) {
          return t('obstructions.wizard.validation.tooLong', {
            max: OBSTRUCTION_CONTRACTOR_PHONE_MAX_LENGTH,
          });
        }
        return null;
      },
      contractorEmail: (value: string) => {
        if (!value || value.trim().length === 0) return null;
        if (value.length > OBSTRUCTION_CONTRACTOR_EMAIL_MAX_LENGTH) {
          return t('obstructions.wizard.validation.tooLong', {
            max: OBSTRUCTION_CONTRACTOR_EMAIL_MAX_LENGTH,
          });
        }
        if (!isValidEmail(value.trim())) {
          return t('obstructions.wizard.validation.emailInvalid');
        }
        return null;
      },
      contractorPermitNumber: (value: string) => {
        if (
          value &&
          value.length > OBSTRUCTION_CONTRACTOR_PERMIT_NUMBER_MAX_LENGTH
        ) {
          return t('obstructions.wizard.validation.tooLong', {
            max: OBSTRUCTION_CONTRACTOR_PERMIT_NUMBER_MAX_LENGTH,
          });
        }
        return null;
      },
      contractorNotes: (value: string) => {
        if (value && value.length > OBSTRUCTION_CONTRACTOR_NOTES_MAX_LENGTH) {
          return t('obstructions.wizard.validation.tooLong', {
            max: OBSTRUCTION_CONTRACTOR_NOTES_MAX_LENGTH,
          });
        }
        return null;
      },
      affectedRoads: (value: ObstructionAffectedRoadInput[]) => {
        if (value.length > OBSTRUCTION_MAX_AFFECTED_ROADS) {
          return t('obstructions.wizard.validation.affectedRoadsTooMany', {
            max: OBSTRUCTION_MAX_AFFECTED_ROADS,
          });
        }
        const hasEmptyLocation = value.some(
          (item) => item.location.trim().length === 0
        );
        if (hasEmptyLocation) {
          return t(
            'obstructions.wizard.validation.affectedRoadLocationRequired'
          );
        }
        const hasTooLongLocation = value.some(
          (item) =>
            item.location.trim().length >
            OBSTRUCTION_AFFECTED_ROAD_LOCATION_MAX_LENGTH
        );
        if (hasTooLongLocation) {
          return t(
            'obstructions.wizard.validation.affectedRoadLocationMaxLength',
            { max: OBSTRUCTION_AFFECTED_ROAD_LOCATION_MAX_LENGTH }
          );
        }
        return null;
      },
    },
  });

  const totalSteps = OBSTRUCTION_STEP_LOGIC.length;

  const goToStep = useCallback(
    (step: number): boolean => {
      if (step < 1 || step > totalSteps) return false;
      form.setFieldValue('currentStep', step);
      return true;
    },
    [form, totalSteps]
  );

  return { form, goToStep };
}
